import { Inject, Injectable, NgZone } from '@angular/core';
import { ISubscription } from 'rxjs-compat/Subscription';
import { IUpgradeLogEntry } from '@fi-shared/models';
import { ReplaySubject } from 'rxjs';

import {
    IBackgroundExceptionEventData,
    IChatConversationIsTypingChangedEventData,
    IChatConversationMessageEventData,
    IChatConversationMessageReadEventData,
    IChecklist2ViewerChangedEventData,
    ICheckListViewerChangedEventData,
    ICommentsUpdatedEventArgs,
    IFormViewerSavedEventData,
    IItemUpdatedEventArgs,
    IItemUpdatedEventData,
    IReactionsUpdatedEventArgs,
    IOnboardingCompletionChangedEventArgs,
    //IModuleNotificationUpdatedEventArgs,
    //INewsFeedUpdateEventArgs,
    //IPersonalNotificationUpdatedEventArgs,
    IProgressEventData,
    //ITaskCompletedUpdatedEventData,
    IUserBusinessUnitsUpdatedEventData,
    IUserOnlineUpdatedEventData,
    IVimeoVideosDataUpdatedEventData,
    IReactionFavoriteUpdatedEventArgs,
} from '@fi-shared/fi-signal-r/fi-signal-r';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FI_BACKEND_CONFIG, IFiBackendConfig } from '@fi-shared/fi-backend/fi-backend.service';
import * as signalRCore from '@microsoft/signalr';
import { FiSignalREvent } from '@fi-shared/fi-signal-r/fi-signal-r-event';

@Injectable()
export class FiSignalRService {
    onConnectionStarted$ = new ReplaySubject<void>(1);

    private subscriptions: ISubscription[] = [];

    dbOperationEvent = new FiSignalREvent<IItemUpdatedEventData>('DbOperation', this.ngZone, (s) =>
        this.subscriptions.push(s),
    );
    //moduleNotificationUpdatedEvent = new FiSignalREvent<IModuleNotificationUpdatedEventArgs>("ModuleNotificationUpdated", this.ngZone, s => this.subscriptions.push(s));
    //newsFeedUpdateEvent = new FiSignalREvent<INewsFeedUpdateEventArgs>("NewsFeedUpdate", this.ngZone, s => this.subscriptions.push(s));
    //personalNotificationUpdatedEvent = new FiSignalREvent<IPersonalNotificationUpdatedEventArgs>("PersonalNotificationUpdated", this.ngZone, s => this.subscriptions.push(s));
    newVersionUpgradeEvent = new FiSignalREvent<IUpgradeLogEntry>('NewVersionUpdate', this.ngZone, (s) =>
        this.subscriptions.push(s),
    );
    commentsUpdatedEvent = new FiSignalREvent<ICommentsUpdatedEventArgs>('CommentsUpdated', this.ngZone, (s) =>
        this.subscriptions.push(s),
    );
    reactionsUpdatedEvent = new FiSignalREvent<IReactionsUpdatedEventArgs>('ReactionsUpdated', this.ngZone, (s) =>
        this.subscriptions.push(s),
    );
    reactionFavoriteUpdated = new FiSignalREvent<IReactionFavoriteUpdatedEventArgs>(
        'ReactionFavoriteUpdated',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );
    //taskCompletedUpdatedEvent = new FiSignalREvent<ITaskCompletedUpdatedEventData>("TaskCompletedUpdated", this.ngZone, s => this.subscriptions.push(s));
    chatConversationMessageReadEvent = new FiSignalREvent<IChatConversationMessageReadEventData>(
        'ChatConversationMessageRead',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );
    chatConversationMessageUpdatedEvent = new FiSignalREvent<IChatConversationMessageEventData>(
        'ConversationMessageUpdated',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );
    userOnlineUpdatedEvent = new FiSignalREvent<IUserOnlineUpdatedEventData>('UserOnlineUpdated', this.ngZone, (s) =>
        this.subscriptions.push(s),
    );
    chatConversationIsTypingChangedEvent = new FiSignalREvent<IChatConversationIsTypingChangedEventData>(
        'ChatConversationIsTypingChanged',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );
    checkListViewerChangedEvent = new FiSignalREvent<ICheckListViewerChangedEventData>(
        'CheckListViewerChanged',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );
    checklist2ViewerChangedEvent = new FiSignalREvent<IChecklist2ViewerChangedEventData>(
        'Checklist2ViewerChanged',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );
    backgroundExceptionEvent = new FiSignalREvent<IBackgroundExceptionEventData>(
        'BackgroundException',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );
    progressEvent = new FiSignalREvent<IProgressEventData>('Progress', this.ngZone, (s) => this.subscriptions.push(s));
    userBusinessUnitsUpdatedEvent = new FiSignalREvent<IUserBusinessUnitsUpdatedEventData>(
        'UserBusinessUnitsUpdated',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );
    vimeoVideosDataUpdatedEvent = new FiSignalREvent<IVimeoVideosDataUpdatedEventData>(
        'VimeoVideosDataUpdated',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );
    formViewerSavedEvent = new FiSignalREvent<IFormViewerSavedEventData>('FormViewerSaved', this.ngZone, (s) =>
        this.subscriptions.push(s),
    );
    itemUpdatedEvent = new FiSignalREvent<IItemUpdatedEventArgs>('ItemUpdated', this.ngZone, (s) =>
        this.subscriptions.push(s),
    );
    onboardingCompletionChangedEvent = new FiSignalREvent<IOnboardingCompletionChangedEventArgs>(
        'OnboardingCompletionChanged',
        this.ngZone,
        (s) => this.subscriptions.push(s),
    );

    constructor(
        private userDataService: FiUserDataService,
        private customerDataService: FiCustomerDataService,
        private ngZone: NgZone,
        @Inject(FI_BACKEND_CONFIG) private backendConfig: IFiBackendConfig,
    ) {}

    private hubConnection: signalRCore.HubConnection;
    init(opts: { isMobileApp: boolean }) {
        const customerId = this.customerDataService.customerData.CustomerId;
        const userId = this.userDataService.getUserId();
        const url =
            this.backendConfig.basePath +
            `/NewsFeedHub?CustomerId=${customerId}&UserId=${userId}&IsMobileApp=${opts.isMobileApp}`;
        this.hubConnection = new signalRCore.HubConnectionBuilder().withAutomaticReconnect().withUrl(url).build();
        this.hubConnection
            .start()
            .then(() => {
                this.initCoreConnection(this.hubConnection);
                this.onConnectionStarted$.next();
            })
            .catch((err) => console.log('Error while starting connection: ' + err));
    }

    private initCoreConnection(connection: signalRCore.HubConnection) {
        this.dbOperationEvent.attachToSignalREvent(connection);
        //this.moduleNotificationUpdatedEvent.attachToSignalREvent(connection);
        //this.newsFeedUpdateEvent.attachToSignalREvent(connection);
        //this.personalNotificationUpdatedEvent.attachToSignalREvent(connection);
        this.newVersionUpgradeEvent.attachToSignalREvent(connection);
        this.commentsUpdatedEvent.attachToSignalREvent(connection);
        this.reactionsUpdatedEvent.attachToSignalREvent(connection);
        this.reactionFavoriteUpdated.attachToSignalREvent(connection);
        //this.taskCompletedUpdatedEvent.attachToSignalREvent(connection);
        this.chatConversationMessageReadEvent.attachToSignalREvent(connection);
        this.chatConversationMessageUpdatedEvent.attachToSignalREvent(connection);
        this.userOnlineUpdatedEvent.attachToSignalREvent(connection);
        this.chatConversationIsTypingChangedEvent.attachToSignalREvent(connection);
        this.checkListViewerChangedEvent.attachToSignalREvent(connection);
        this.checklist2ViewerChangedEvent.attachToSignalREvent(connection);
        this.backgroundExceptionEvent.attachToSignalREvent(connection);
        this.progressEvent.attachToSignalREvent(connection);
        this.vimeoVideosDataUpdatedEvent.attachToSignalREvent(connection);
        this.formViewerSavedEvent.attachToSignalREvent(connection);
        this.itemUpdatedEvent.attachToSignalREvent(connection);
        this.onboardingCompletionChangedEvent.attachToSignalREvent(connection);
    }
    //endregion

    closeConnection() {
        if (this.hubConnection) {
            this.hubConnection.stop();
            this.hubConnection = undefined;
        }

        this.subscriptions.forEach((s) => s.unsubscribe());
        this.subscriptions.length = 0;
    }
}
